import type { DocumentSnapshot as RNDocumentSnapshot } from '@react-native-firebase/firestore';
import type { DocumentData, DocumentSnapshot } from 'firebase/firestore';
import throttle from 'lodash.throttle';

import { ApiNotificationType } from '@izimi/api';

import { notificationItems } from '../../notifications/notificationItems';

type CacheInvalidationFns = {
  invalidateCommunicationsCache: () => void;
  invalidateNotificationsCache: () => void;
  invalidateLastNotificationsCache: () => void;
  invalidateContactsCache: () => void;
  invalidateContactRequestsCache: () => void;
  invalidateVaultInfoCache: () => void;
  invalidateDocumentsUnSeenListCache: () => void;
  invalidateDocumentsVaultListCache: () => void;
  invalidateDocumentsSharedFromNotaryListCache: () => void;
  invalidateDocumentsSharedToYouListCache: () => void;
  invalidateSharedItemsCache: () => void;
};

export const RealtimeDatabaseEventKeys = {
  BOX_DOCUMENT_COPIED_TO_VAULT: 'BOX_DOCUMENT_COPIED_TO_VAULT',
} as const;

type CacheInvalidationProps = {
  data: DocumentSnapshot | RNDocumentSnapshot;
  cacheInvalidationFns: CacheInvalidationFns;
  onEvent?: (event: Record<string, string>) => void;
};

let cachedFields: DocumentData | undefined;

type ThrottledCallbackKeys = 'BOX_COPIED_TO_VAULT';

const throttledCacheInvalidationsCallbacks: Partial<Record<ThrottledCallbackKeys, () => void>> = {};

export const snapshotHandler = ({
  data,
  cacheInvalidationFns,
  onEvent,
}: CacheInvalidationProps) => {
  const fields = data.data();
  let invalidateNotificationCache = false;

  if (!fields || cachedFields == null) {
    cachedFields = fields;
    return;
  }

  // We have to throttle this callback because the user can copy many files at once, triggering a lot of nearly simultaneous events.
  if (throttledCacheInvalidationsCallbacks.BOX_COPIED_TO_VAULT == null) {
    throttledCacheInvalidationsCallbacks.BOX_COPIED_TO_VAULT = throttle(
      () => {
        cacheInvalidationFns.invalidateCommunicationsCache();
        cacheInvalidationFns.invalidateSharedItemsCache();
        cacheInvalidationFns.invalidateDocumentsSharedToYouListCache();
        cacheInvalidationFns.invalidateDocumentsVaultListCache();
        cacheInvalidationFns.invalidateVaultInfoCache();
      },
      1000,
      { trailing: true },
    );
  }

  Object.entries(fields).forEach(([key, value]) => {
    if (cachedFields?.[key]?.toString() === value.toString()) {
      return null;
    }

    if (notificationItems[key as ApiNotificationType] != null) {
      invalidateNotificationCache = true;
    }

    if (onEvent) {
      onEvent({ [key]: value });
    }

    switch (key) {
      case RealtimeDatabaseEventKeys.BOX_DOCUMENT_COPIED_TO_VAULT:
        return throttledCacheInvalidationsCallbacks.BOX_COPIED_TO_VAULT?.();
      case ApiNotificationType.ContactRequestAccepted:
      case ApiNotificationType.ContactRequestReceived:
      case ApiNotificationType.ContactRemoveReceived:
        cacheInvalidationFns.invalidateContactsCache();
        return cacheInvalidationFns.invalidateContactRequestsCache();
      case ApiNotificationType.VaultTransferReceived:
        return cacheInvalidationFns.invalidateVaultInfoCache();
      case ApiNotificationType.PushedDocumentsReceived:
        cacheInvalidationFns.invalidateSharedItemsCache();
        return cacheInvalidationFns.invalidateDocumentsSharedFromNotaryListCache();
      case ApiNotificationType.CommunicationReceived:
        cacheInvalidationFns.invalidateSharedItemsCache();
        return cacheInvalidationFns.invalidateCommunicationsCache();
      case ApiNotificationType.StructuredCommunicationReceived:
        return cacheInvalidationFns.invalidateCommunicationsCache();
      case ApiNotificationType.SharedDocumentsReceived:
      case ApiNotificationType.SharedDocumentUnshared:
      case ApiNotificationType.SharedDocumentUnsharedByRecipient:
      case ApiNotificationType.SharedDocumentsAccepted:
      case ApiNotificationType.DocumentUploadedByThirdParty:
      case ApiNotificationType.NotaryDocumentUnshared:
        cacheInvalidationFns.invalidateDocumentsUnSeenListCache();
        cacheInvalidationFns.invalidateSharedItemsCache();
        cacheInvalidationFns.invalidateDocumentsVaultListCache();
        return cacheInvalidationFns.invalidateDocumentsSharedToYouListCache();
      default:
        return null;
    }
  });

  if (invalidateNotificationCache) {
    cacheInvalidationFns.invalidateLastNotificationsCache();
    cacheInvalidationFns.invalidateNotificationsCache();
  }

  cachedFields = fields;
};
